import React, { useEffect, useRef, useState } from "react";
import styles from "./UserProfileBar.module.scss";
import ArrowLeft from "../../assets/svg/lilArrowLeft.svg";
import FAQ from "../../assets/svg/FAQ.svg";
import Settings from "../../assets/svg/settings.svg";
import ProfileAvatar from "../../assets/images/Profile image.jpg";
import DefaultProfileAvatar from "../../assets/images/DefaultProfileAvatar.svg";
import { useActions } from "../../hooks/useActions";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import UserProfileModal from "./UserProfileModal/UserProfileModal";
import { useNavigate, useLocation } from "react-router-dom";
import SettingsModal from "./SettingsModal/SettingsModal";
import { datasets } from "../../constants/mapConstants";
import useSearchParams from "../../hooks/useSearchParams";
import { useTranslation } from "react-i18next";
import UserProfileBarFilterModal from "./UserProfileBarFilterModal";
import ButtonModal from "../../share/ButtonModal";
import useAppContext from "../../hooks/useAppContext";
import Notification from "../../share/Notification";
import styled from "@emotion/styled";
import { Empty } from "antd";
import { colors, icons } from "../../constants";
import { RouteNames } from "../../routes";
import useStoreSnippets from "../../hooks/useStoreSnippets";
import PopupBar from "../PopupBar/PopupBar";

const StyledProfileButtonWrapper = styled.div``;
const SettingsButtonWrapper = styled.div``;
const FAQButtonWrapper = styled.div``;
const NotificationsButtonWrapper = styled.div``;

interface IProfileMenu {
  isWithFilterPopupButton?: boolean;
}

const useProfileMenuState = (isWithFilterPopupButton: boolean) => {
  const {
    setIsFilterActive,
    setProfileModal,
    setSettingModal,
    toggleFilter,
    setIsWarned,
    setIsListViewActive,
  } = useActions();
  const {
    isFilterBarActive,
    isProfileModalActive,
    isSettingsActive,
    isListViewActive,
  } = useTypedSelector((state) => state.mapInterface);
  const previousFilterBarState = useRef(isFilterBarActive);

  useEffect(() => {
    if (!isListViewActive) {
      previousFilterBarState.current = isFilterBarActive;
    }
  }, [isFilterBarActive, isListViewActive]);

  const navigate = useNavigate();
  const location = useLocation();
  const { isGuestAccount, isAuthWarned } = useStoreSnippets();

  const {
    typeFilterArray,
    ccFilterArray,
    constructionType,
    stageBuilding,
    stageDocumentation,
    region,
    to_date,
    activeSuggestions,
  } = useTypedSelector((state) => state.mapFilters);

  const [activeFiltersModal, setActiveFiltersModal] = useState(false);

  const activeFiltersCount =
    typeFilterArray.length +
    ccFilterArray.length +
    constructionType.length +
    stageBuilding.length +
    stageDocumentation.length +
    region.length +
    activeSuggestions.length +
    (to_date ? 1 : 0);

  const handleToggleFilterPanel = () => {
    setIsFilterActive(!isFilterBarActive);
    toggleFilter("");
  };

  const handleToggleListView = () => {
    const nextState = !isListViewActive;
    if (nextState) {
      previousFilterBarState.current = isFilterBarActive;
    }
    setIsListViewActive(nextState);
    if (nextState) {
      setIsFilterActive(true);
    } else {
      setIsFilterActive(previousFilterBarState.current);
    }
  };

  const isFiltersPopupButton = !isFilterBarActive && isWithFilterPopupButton;

  const { getSearchParam } = useSearchParams();
  const dataset = getSearchParam("dataset");

  const { t: tFilterAction } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.filtersActions",
  });

  const { t: tNotifications } = useTranslation("translation", {
    keyPrefix: "notifications",
  });

  const { t: tBar } = useTranslation("translation", {
    keyPrefix: "userProfileBar.warningBar",
  });

  const { lastNotifications, isNewNotifications, setIsNewNotifications } =
    useAppContext();

  const handleClickLoginButton = () => {
    setProfileModal(false);
    navigate(RouteNames.LOGIN);
  };

  const handleFeedbackClick = () => {
    setProfileModal(false);
    navigate(RouteNames.FEEDBACK_PAGE);
  };

  const handleSettingsClick = () => {
    setSettingModal(!isSettingsActive);
    setProfileModal(false);
  };

  const handleProfileButtonClick = () => {
    setProfileModal(!isProfileModalActive);
    setSettingModal(false);
    toggleFilter("");
  };

  const resetQuickModals = () => {
    setIsNewNotifications(false);
    setProfileModal(false);
    setSettingModal(false);
    setActiveFiltersModal(false);
  };

  const isMain = location.pathname === RouteNames.AIMAP;

  return {
    isFilterBarActive,
    isProfileModalActive,
    isSettingsActive,
    isListViewActive,
    handleToggleFilterPanel,
    handleToggleListView,
    handleClickLoginButton,
    handleFeedbackClick,
    handleSettingsClick,
    handleProfileButtonClick,
    resetQuickModals,
    isGuestAccount,
    isAuthWarned,
    activeFiltersModal,
    setActiveFiltersModal,
    activeFiltersCount,
    dataset,
    tFilterAction,
    tNotifications,
    tBar,
    lastNotifications,
    isNewNotifications,
    setIsWarned,
    isFiltersPopupButton,
    isMain,
  };
};

type ProfileMenuState = ReturnType<typeof useProfileMenuState>;

const UserSettingsNavigation: React.FC<{
  state: ProfileMenuState;
  variant: "default" | "inline";
}> = ({ state, variant }) => {
  const {
    resetQuickModals,
    lastNotifications,
    tNotifications,
    isNewNotifications,
    handleFeedbackClick,
    handleSettingsClick,
    handleProfileButtonClick,
    handleClickLoginButton,
    isGuestAccount,
  } = state;

  return (
    <nav
      className={
        variant === "default"
          ? styles.userSettingsMenu
          : styles.inlineUserSettingsMenu
      }
      onClick={(e) => e.stopPropagation()}
    >
      <NotificationsButtonWrapper>
        <ButtonModal
          triggerReactNode={
            <button onClick={resetQuickModals} className={styles.userMenuBtn}>
              <img
                src={
                  isNewNotifications
                    ? icons.ActiveNotification
                    : icons.Notification
                }
                alt="Notifications"
              />
            </button>
          }
          modalId="notifications"
          modalWidth={400}
        >
          {lastNotifications.length ? (
            <StyledNotificationList>
              <h3>{tNotifications("title")}</h3>
              {[...lastNotifications]
                .sort((a, b) => b.timeStamp - a.timeStamp)
                .map(({ type, args, timeStamp }, index) => (
                  <Notification
                    key={index}
                    type={type}
                    description={args.description}
                    message={args.message}
                    timeStamp={timeStamp}
                  />
                ))}
            </StyledNotificationList>
          ) : (
            <Empty description="No notifications" />
          )}
        </ButtonModal>
      </NotificationsButtonWrapper>
      <FAQButtonWrapper>
        <button onClick={handleFeedbackClick} className={styles.userMenuBtn}>
          <img src={FAQ} alt="FAQ" />
        </button>
      </FAQButtonWrapper>
      <SettingsButtonWrapper>
        <button onClick={handleSettingsClick} className={styles.userMenuBtn}>
          <img src={Settings} alt="Settings" />
        </button>
      </SettingsButtonWrapper>

      {!isGuestAccount ? (
        <StyledProfileButtonWrapper>
          <button
            onClick={handleProfileButtonClick}
            className={styles.profileSettingsTrigger}
          >
            <img src={ProfileAvatar} alt="UserProfile" />
          </button>
        </StyledProfileButtonWrapper>
      ) : (
        <StyledProfileButtonWrapper>
          <button
            onClick={handleClickLoginButton}
            className={styles.profileSettingsLogin}
          >
            <img src={DefaultProfileAvatar} alt="UserProfile" />
          </button>
        </StyledProfileButtonWrapper>
      )}
    </nav>
  );
};

const StyledNotificationList = styled.div`
  display: grid;
  h3 {
    color: ${colors.darkD400};
    /* Heading 2/Semibold 24 */
    font-family: Montserrat;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 125% */
    margin-bottom: 24px;
  }
`;

const ProfileMenu: React.FC<IProfileMenu> = ({
  isWithFilterPopupButton = false,
}) => {
  const state = useProfileMenuState(isWithFilterPopupButton);

  if (state.isListViewActive) {
    return null;
  }

  return (
    <div
      className={
        state.isFilterBarActive && !state.isListViewActive
          ? `${styles.profileMenu} ${styles.smallerNav}`
          : styles.profileMenu
      }
    >
      <div className={styles.menuBar}>
        {state.isMain && (
          <div className={styles.profileMenuWrapper}>
            <div
              className={
                state.isFilterBarActive
                  ? `${styles.viewToggleButtons} ${styles.doubleToggleGroup}`
                  : styles.viewToggleButtons
              }
            >
              <button
                type="button"
                className={
                  state.isFilterBarActive
                    ? `${styles.filterTriggerBtn} ${styles.smallBtn}`
                    : styles.filterTriggerBtn
                }
                onClick={state.handleToggleFilterPanel}
              >
                <img
                  className={styles.filterTriggerIcon}
                  src={ArrowLeft}
                  alt="close"
                />
                <span>{state.tFilterAction("showAllFilters")}</span>
              </button>
              <button
                type="button"
                className={styles.listViewToggleButton}
                onClick={state.handleToggleListView}
                aria-label="Show list view"
              >
                <img
                  src={
                    state.isListViewActive
                      ? icons.ListViewActive
                      : icons.ListView
                  }
                  alt="Show list view"
                />
              </button>
            </div>
            {state.isFiltersPopupButton &&
              state.dataset === datasets.objects && (
                <button
                  onClick={() => state.setActiveFiltersModal(true)}
                  className={styles.filterTriggerBtn}
                >
                  {state.tFilterAction("filters")}
                  {state.activeFiltersCount > 0 && (
                    <>&nbsp;&bull;&nbsp;{state.activeFiltersCount}</>
                  )}
                </button>
              )}
          </div>
        )}
        <UserSettingsNavigation state={state} variant="default" />
      </div>
      {state.isSettingsActive && <SettingsModal />}
      {state.isProfileModalActive && <UserProfileModal />}
      <UserProfileBarFilterModal
        setActiveFiltersModal={state.setActiveFiltersModal}
        activeFiltersModal={state.activeFiltersModal}
      />
      {state.isGuestAccount &&
        !state.isAuthWarned &&
        state.isMain &&
        state.dataset !== datasets.cybersecurity && (
          <PopupBar
            message={state.tBar("message")}
            btnText={state.tBar("btn")}
            onBtnClick={state.handleClickLoginButton}
            onClose={() => {
              state.setIsWarned(true);
            }}
          />
        )}
    </div>
  );
};

export const UserProfileListControls: React.FC = () => {
  const state = useProfileMenuState(false);

  if (!state.isListViewActive) {
    return null;
  }

  return (
    <div className={styles.inlineContainer}>
      <UserSettingsNavigation state={state} variant="inline" />
      {state.isSettingsActive && <SettingsModal />}
      {state.isProfileModalActive && <UserProfileModal />}
    </div>
  );
};

export default ProfileMenu;
